/**
 * Component: sa-shop-product
 * Handles:
 *   1. Color swatch click → variant switch (image, price line 1 when auto, buy link)
 *   2. Mobile carousel prev/next + progress
 *   3. Mobile 2x2 grid mode (sa-shop-product--mobile-grid) skips carousel logic
 *   4. [DTC-724] Add-to-cart mode buy button (AJAX add of selected variant, qty 1,
 *      button greyed while pending to prevent duplicate adds on slow networks)
 */
if (!customElements.get('sa-shop-product')) {

  class SaShopProduct extends HTMLElement {
    constructor() {
      super();
      this.handleSwatchClick = this.handleSwatchClick.bind(this);
      this.handlePrev = this.handlePrev.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleTaglineMore = this.handleTaglineMore.bind(this);
      this.handleAtcClick = this.handleAtcClick.bind(this);
      this.handlePageShow = this.handlePageShow.bind(this);
    }

    connectedCallback() {
      this.isMobileGrid = this.classList.contains('sa-shop-product--mobile-grid');
      this.track = this.querySelector('.sa-shop-product__track');
      this.prevBtn = this.querySelector('.sa-shop-product__ctrl-btn--prev');
      this.nextBtn = this.querySelector('.sa-shop-product__ctrl-btn--next');
      this.progressBar = this.querySelector('.sa-shop-product__progress-bar');

      this.addEventListener('click', this.handleSwatchClick);
      this.addEventListener('click', this.handleTaglineMore);
      this.addEventListener('click', this.handleAtcClick);
      window.addEventListener('pageshow', this.handlePageShow);
      if (this.prevBtn) this.prevBtn.addEventListener('click', this.handlePrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.handleNext);
      if (!this.isMobileGrid && this.track) {
        this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      }

      if (!this.isMobileGrid) this.updateProgress();
      this.initTaglines();
      this.alignInfoHeights();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleSwatchClick);
      this.removeEventListener('click', this.handleTaglineMore);
      this.removeEventListener('click', this.handleAtcClick);
      window.removeEventListener('pageshow', this.handlePageShow);
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.handlePrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.handleNext);
      if (this.track) this.track.removeEventListener('scroll', this.handleScroll);
    }

    handleSwatchClick(e) {
      const swatch = e.target.closest('.sa-shop-product__swatches .option-item');
      if (!swatch) return;

      const card = swatch.closest('.sa-shop-product__card');
      if (!card) return;

      const value = swatch.dataset.value || '';
      const position = parseInt(swatch.parentElement.dataset.position || '', 10);
      if (!value || !position) return;

      const activeImg = card.querySelector('.product-img.active');
      if (!activeImg) return;

      const parts = (activeImg.dataset.title || '').split(' / ');
      parts[position - 1] = value;
      const newTitle = parts.join(' / ');

      const allImgs = card.querySelectorAll('.product-img');
      const newImg = Array.from(allImgs).find(el => el.dataset.title === newTitle);
      if (!newImg) return;

      const variantId = newImg.dataset.id || '';
      const available = newImg.dataset.available === 'true';
      const price = newImg.dataset.price || '';
      const comparePrice = newImg.dataset.comparePrice || '';

      activeImg.classList.remove('active');
      newImg.classList.add('active');

      const prevActive = swatch.parentElement.querySelector('.option-item.active');
      if (prevActive) prevActive.classList.remove('active');
      swatch.classList.add('active');

      if (card.dataset.autoPrice === 'true') {
        const priceAmount = card.querySelector('.sa-shop-product__price-amount');
        if (priceAmount && price) priceAmount.textContent = price;

        const comparePriceEl = card.querySelector('.sa-shop-product__compare-price');
        if (comparePriceEl) {
          if (comparePrice) {
            comparePriceEl.textContent = comparePrice;
            comparePriceEl.style.display = '';
          } else {
            comparePriceEl.style.display = 'none';
          }
        }
      }

      const buyBtn = card.querySelector('.sa-shop-product__btn-buy');
      if (buyBtn) {
        if (buyBtn.classList.contains('sa-shop-product__btn-buy--atc')) {
          buyBtn.dataset.variantId = variantId;
          if (available && variantId) {
            buyBtn.disabled = false;
            buyBtn.classList.remove('sa-shop-product__btn-buy--disabled');
          } else {
            buyBtn.disabled = true;
            buyBtn.classList.add('sa-shop-product__btn-buy--disabled');
          }
        } else {
          const cartBase = buyBtn.dataset.cartBase;
          if (available && cartBase && variantId) {
            buyBtn.href = cartBase + '/' + variantId + ':1';
            buyBtn.classList.remove('sa-shop-product__btn-buy--disabled');
          } else {
            buyBtn.href = '#';
            buyBtn.classList.add('sa-shop-product__btn-buy--disabled');
          }
        }
      }
    }

    // ── [DTC-724] Add-to-cart mode ─────────────────────────────────────────

    handleAtcClick(e) {
      const btn = e.target.closest('.sa-shop-product__btn-buy--atc');
      if (!btn || btn.disabled || btn.classList.contains('sa-shop-product__btn-buy--loading')) return;
      e.preventDefault();

      const variantId = parseInt(btn.dataset.variantId || '', 10);
      if (!variantId) return;

      btn.classList.add('sa-shop-product__btn-buy--loading');
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const canRenderSections = cart && typeof cart.getSectionsToRender === 'function';
      const body = {
        items: [{ id: variantId, quantity: 1 }],
        sections: canRenderSections ? cart.getSectionsToRender().map((section) => section.id) : [],
        sections_url: window.location.pathname
      };

      fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      })
        .then((res) => res.json())
        .then((response) => {
          if (response.status || response.errors) {
            console.error('sa-shop-product add to cart failed:', response.description || response.errors);
            this.resetAtcButton(btn);
            return;
          }
          if (cart) {
            this.updateCartCount();
            this.updateCartSections(cart, response);
            if (typeof cart.open === 'function') cart.open();
            this.resetAtcButton(btn);
          } else {
            // 全局设置为 go_to_cart_page 时页面无 cart-notification/cart-drawer，
            // 跟随主题惯例（theme.js addItemsToCart 同场景）直接进入购物车页；
            // 跳转完成前不复位按钮，防止重复点击造成多次加购
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          }
        })
        .catch((error) => {
          console.error('sa-shop-product add to cart failed:', error);
          this.resetAtcButton(btn);
        });
    }

    resetAtcButton(btn) {
      btn.classList.remove('sa-shop-product__btn-buy--loading');
      btn.setAttribute('aria-busy', 'false');
      // --disabled 类是售罄态的事实来源（Liquid 初始渲染 + handleSwatchClick 维护），
      // 请求期间 swatch 可能已切到售罄变体，不能无条件重新启用
      if (!btn.classList.contains('sa-shop-product__btn-buy--disabled')) {
        btn.disabled = false;
      }
    }

    // bfcache 后退恢复时页面快照里按钮仍是 loading 置灰态（跳转前刻意不复位），
    // 沿用 theme.js:2265 checkout 按钮同类修复的 pageshow 惯例复位
    handlePageShow(e) {
      if (!e.persisted) return;
      this.querySelectorAll('.sa-shop-product__btn-buy--atc.sa-shop-product__btn-buy--loading')
        .forEach((btn) => this.resetAtcButton(btn));
    }

    updateCartCount() {
      fetch('/cart.json')
        .then((res) => res.json())
        .then((cartData) => {
          if (cartData.item_count === undefined) return;
          document.querySelectorAll('.cart-count').forEach((el) => {
            if (el.classList.contains('cart-count-drawer')) {
              el.innerHTML = `(${cartData.item_count})`;
            } else {
              el.innerHTML = cartData.item_count > 100 ? '~' : cartData.item_count;
            }
          });
          const headerTotal = document.querySelector('header-total-price');
          if (headerTotal) headerTotal.updateTotal(cartData);
          const cartFreeShip = document.querySelector('free-ship-progress-bar');
          if (cartFreeShip) cartFreeShip.init(cartData.items_subtotal_price);
        })
        .catch((error) => {
          console.error('Error updating cart count:', error);
        });
    }

    updateCartSections(cart, response) {
      if (!response.sections || typeof cart.getSectionsToRender !== 'function') return;
      cart.getSectionsToRender().forEach((section) => {
        const elementToReplace = document.getElementById(section.id);
        const html = new DOMParser().parseFromString(response.sections[section.id] || '', 'text/html');
        const inner = html.querySelector('#minicart-form');
        if (elementToReplace && inner) {
          elementToReplace.innerHTML = inner.innerHTML;
        }
      });
      if (typeof cart.cartAction === 'function') cart.cartAction();
    }

    initTaglines() {
      const taglines = this.querySelectorAll('.sa-shop-product__tagline');
      taglines.forEach(el => {
        const style = getComputedStyle(el);
        let lh = parseFloat(style.lineHeight);
        if (isNaN(lh)) lh = parseFloat(style.fontSize) * 1.4;
        if (el.scrollHeight > lh * 3 + 1) {
          el.classList.add('sa-shop-product__tagline--clamped');
        }
      });
    }

    handleTaglineMore(e) {
      const btn = e.target.closest('.sa-shop-product__tagline-more');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const tagline = btn.closest('.sa-shop-product__tagline');
      if (tagline) {
        tagline.classList.remove('sa-shop-product__tagline--clamped');
        tagline.classList.add('sa-shop-product__tagline--expanded');
        this.alignInfoHeights();
      }
    }

    alignInfoHeights() {
      const infos = this.querySelectorAll('.sa-shop-product__info');
      if (!infos.length) return;
      infos.forEach(el => { el.style.minHeight = ''; });
      let max = 0;
      infos.forEach(el => { max = Math.max(max, el.offsetHeight); });
      if (max > 0) {
        infos.forEach(el => { el.style.minHeight = max + 'px'; });
      }
    }

    // ── Mobile carousel ────────────────────────────────────────────────────

    getCardWidth() {
      if (!this.track) return 200;
      const card = this.track.querySelector('.sa-shop-product__card');
      if (!card) return 200;
      const gap = parseFloat(getComputedStyle(this.track).gap) || 10;
      return card.offsetWidth + gap;
    }

    handlePrev() {
      if (this.isMobileGrid) return;
      if (!this.track) return;
      this.track.scrollBy({ left: -this.getCardWidth(), behavior: 'smooth' });
    }

    handleNext() {
      if (this.isMobileGrid) return;
      if (!this.track) return;
      this.track.scrollBy({ left: this.getCardWidth(), behavior: 'smooth' });
    }

    handleScroll() {
      if (this.isMobileGrid) return;
      this.updateProgress();
    }

    updateProgress() {
      if (this.isMobileGrid) return;
      if (!this.track || !this.progressBar) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const maxScroll = scrollWidth - clientWidth;
      if (maxScroll <= 0) {
        this.progressBar.style.width = '100%';
        if (this.prevBtn) this.prevBtn.disabled = true;
        if (this.nextBtn) this.nextBtn.disabled = true;
        return;
      }
      const pct = (scrollLeft / maxScroll) * 100;
      this.progressBar.style.width = Math.min(100, Math.max(0, pct)) + '%';
      if (this.prevBtn) this.prevBtn.disabled = scrollLeft <= 1;
      if (this.nextBtn) this.nextBtn.disabled = scrollLeft >= maxScroll - 1;
    }
  }

  customElements.define('sa-shop-product', SaShopProduct);
}
